import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { GroupedPhase } from '../utils/phaseGrouping';

function formatSeconds(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m === 0) return `${sec}s`;
  return sec > 0 ? `${m}:${sec.toString().padStart(2, '0')}` : `${m} min`;
}

interface Phase {
  phase_name?: string;
  name?: string;
  phase_type?: string;
  duration_minutes?: number;
  duration_seconds?: number;
  distance_meters?: number;
  distance_km?: number;
  target_pace_min_per_km?: string;
  target_pace_max_per_km?: string;
  target_pace?: string;
  target_hr_min?: number;
  target_hr_max?: number;
  hr_zone?: number;
  instructions?: string;
  description?: string;
  coaching_cues?: string[];
  key_focus_points?: string[];  // Legacy field name
  repeat_count?: number;
  rest_after_seconds?: number;
  target_cadence_rpm?: number;
  recovery_hr_target?: number;
}

interface CollapsibleRunPhaseProps {
  phase?: Phase;  // Legacy: Single phase
  groupedPhase?: GroupedPhase;  // New: Grouped phases
  index: number;
  userHrZones?: {
    zone1Min: number;
    zone1Max: number;
    zone2Min: number;
    zone2Max: number;
    zone3Min: number;
    zone3Max: number;
    zone4Min: number;
    zone4Max: number;
    zone5Min: number;
    zone5Max: number;
  } | null;
}

/**
 * 🆕 JAN 29, 2026: Collapsible Run Phase Component
 * 🆕 JAN 31, 2026: Enhanced to support grouped repeating segments
 * 
 * Saves vertical space by collapsing guidance into drawer.
 * Header shows: Phase name + duration + metrics (always visible)
 * Body shows: Guidance text + KEY FOCUS POINTS (collapsible)
 * 
 * For grouped phases (strides, intervals):
 * - Header shows: "Strides (4x)" or "10K Pace Intervals (3x)"
 * - Body shows: ONE cycle with metrics and guidance
 */
export const CollapsibleRunPhase: React.FC<CollapsibleRunPhaseProps> = ({ 
  phase, 
  groupedPhase, 
  index, 
  userHrZones 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Determine if this is a grouped phase or single phase
  const isGroup = groupedPhase?.type === 'group' || groupedPhase?.type === 'interval_block';
  
  // 🔧 JAN 31, 2026: Enhanced guard with better error reporting
  if (!phase && !groupedPhase) {
    console.error('CollapsibleRunPhase: No props provided at all');
    return null;
  }
  
  // For grouped phases, verify phases array exists
  if (isGroup && (!groupedPhase?.phases || groupedPhase.phases.length === 0)) {
    console.error('CollapsibleRunPhase: Grouped phase has no phases array', { 
      groupedPhase,
      hasPhases: !!groupedPhase?.phases,
      phasesLength: groupedPhase?.phases?.length
    });
    
    // 🔧 FALLBACK: Try to render as single phase if grouped phase is malformed
    // This prevents the entire run plan from crashing
    if (phase) {
      console.warn('   → Falling back to single phase prop');
      // Continue with phase instead of returning null
    } else {
      return null; // No data at all, skip this phase
    }
  }
  
  // 🔧 FEB 1, 2026: Fixed phase selection logic
  // groupedPhase.phases[0] exists for BOTH 'single' and 'group' types
  // Don't check isGroup - just use phases[0] directly
  const phaseToDisplay = groupedPhase?.phases?.[0] || phase;

  // Final guard: If no phase data at all, return null
  if (!phaseToDisplay) {
    console.error('CollapsibleRunPhase: phaseToDisplay is undefined after selection', { 
      phase, 
      groupedPhase,
      isGroup,
      phases: groupedPhase?.phases
    });
    return null;
  }

  // Get phase name (use grouped name if available)
  const phaseName = isGroup 
    ? groupedPhase!.phaseName
    : (phaseToDisplay.phase_name || phaseToDisplay.name || 
       (phaseToDisplay.phase_type === 'warmup' ? 'Warm-up' :
        phaseToDisplay.phase_type === 'cooldown' ? 'Cooldown' :
        phaseToDisplay.phase_type === 'recovery' ? 'Recovery' :
        phaseToDisplay.phase_type === 'interval' ? 'Interval' :
        phaseToDisplay.phase_type === 'tempo' ? 'Tempo' :
        phaseToDisplay.phase_type === 'steady_state' ? 'Steady Run' :
        'Run'));

  // Get repeat count — from grouping utility OR from phase's own repeat_count
  const repeatCount = groupedPhase?.repeatCount || phaseToDisplay.repeat_count || 1;

  // Get duration (for groups: single cycle duration, not total)
  // For short phases (<60s), show seconds; otherwise show minutes
  const durationDisplay = phaseToDisplay.duration_minutes
    ? `${phaseToDisplay.duration_minutes} min`
    : phaseToDisplay.duration_seconds
      ? (phaseToDisplay.duration_seconds < 60
          ? `${phaseToDisplay.duration_seconds}s`
          : `${Math.round(phaseToDisplay.duration_seconds / 60)} min`)
      : null;

  // Get distance (for groups: single cycle distance)
  const distance = phaseToDisplay.distance_meters 
    ? `${(phaseToDisplay.distance_meters / 1000).toFixed(1)} km`
    : phaseToDisplay.distance_km 
    ? `${phaseToDisplay.distance_km.toFixed(1)} km`
    : null;

  // Get pace
  const pace = phaseToDisplay.target_pace_min_per_km && phaseToDisplay.target_pace_max_per_km
    ? `${phaseToDisplay.target_pace_min_per_km}-${phaseToDisplay.target_pace_max_per_km}/km`
    : phaseToDisplay.target_pace_min_per_km
    ? `${phaseToDisplay.target_pace_min_per_km}/km`
    : phaseToDisplay.target_pace
    ? `${phaseToDisplay.target_pace}/km`
    : null;

  // Get HR target
  const hrTarget = phaseToDisplay.target_hr_min && phaseToDisplay.target_hr_max
    ? `${phaseToDisplay.target_hr_min}-${phaseToDisplay.target_hr_max} bpm`
    : userHrZones && phaseToDisplay.hr_zone ? (() => {
        const zone = phaseToDisplay.hr_zone;
        switch (zone) {
          case 1: return `${userHrZones.zone1Min}-${userHrZones.zone1Max} bpm`;
          case 2: return `${userHrZones.zone2Min}-${userHrZones.zone2Max} bpm`;
          case 3: return `${userHrZones.zone3Min}-${userHrZones.zone3Max} bpm`;
          case 4: return `${userHrZones.zone4Min}-${userHrZones.zone4Max} bpm`;
          case 5: return `${userHrZones.zone5Min}-${userHrZones.zone5Max} bpm`;
          default: return null;
        }
      })()
    : null;

  // Get guidance text
  const guidanceText = phaseToDisplay.instructions || phaseToDisplay.description || '';
  
  // Get key focus points (check both field names)
  const keyFocusPoints = phaseToDisplay.coaching_cues || phaseToDisplay.key_focus_points || [];

  return (
    <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden bg-white">
      {/* Header - Always Visible */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h4 className="text-base font-semibold text-gray-900">{phaseName}</h4>
            {/* Repeat count badge — from grouping OR from phase.repeat_count */}
            {(repeatCount > 1) && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                {repeatCount}x
              </span>
            )}
          </div>
          {durationDisplay && <span className="text-sm text-gray-600">{durationDisplay}{repeatCount > 1 && ' each'}</span>}
        </div>

        {/* For repeating phases, show "per cycle" clarification */}
        {repeatCount > 1 && (
          <div className="mb-3 text-xs text-gray-500">
            Metrics shown are per cycle. Repeat {repeatCount} times{phaseToDisplay.rest_after_seconds ? ` with ${formatSeconds(phaseToDisplay.rest_after_seconds)} recovery between` : ''}.
          </div>
        )}

        {/* Metrics Grid */}
        <div className="grid grid-cols-3 gap-2 text-sm">
          {distance && (
            <div>
              <div className="text-gray-500 text-xs">Distance</div>
              <div className="font-medium text-gray-900">{distance}</div>
            </div>
          )}
          {pace && (
            <div>
              <div className="text-gray-500 text-xs">Pace</div>
              <div className="font-medium text-gray-900">{pace}</div>
            </div>
          )}
          {hrTarget && (
            <div>
              <div className="text-gray-500 text-xs">Heart Rate</div>
              <div className="font-medium text-gray-900">{hrTarget}</div>
            </div>
          )}
        </div>

        {/* Interval details: cadence & recovery HR */}
        {(phaseToDisplay.target_cadence_rpm || phaseToDisplay.recovery_hr_target) && (
          <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500 italic">
            {phaseToDisplay.target_cadence_rpm && (
              <span>Target cadence: {phaseToDisplay.target_cadence_rpm} spm</span>
            )}
            {phaseToDisplay.recovery_hr_target && (
              <span>Recovery HR target: {phaseToDisplay.recovery_hr_target} bpm</span>
            )}
          </div>
        )}

        {/* Expand/Collapse Button */}
        {(guidanceText || keyFocusPoints.length > 0) && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-3 w-full flex items-center justify-between text-sm text-[#1F6C4F] hover:text-[#1F4D2A] transition-colors"
          >
            <span className="font-medium">Guidance</span>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        )}
      </div>

      {/* Collapsible Body - Guidance */}
      {isExpanded && (guidanceText || keyFocusPoints.length > 0) && (
        <div className="px-4 pb-4 pt-0 border-t border-gray-100">
          {/* Guidance Text */}
          {guidanceText && (
            <p className="text-sm text-gray-700 mb-3 leading-relaxed">{guidanceText}</p>
          )}

          {/* Key Focus Points */}
          {keyFocusPoints.length > 0 && (
            <div className="bg-blue-50 rounded-lg p-3">
              <h5 className="text-xs font-semibold text-blue-900 uppercase tracking-wide mb-2">
                Key Focus Points
              </h5>
              <ul className="space-y-1.5">
                {keyFocusPoints.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-blue-800">
                    <span className="text-blue-600 mt-0.5">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
